import { CATALOG } from './catalog';
import type { AssistantSelections } from './types';

/**
 * Max prompt length — mirrors AvatarCreate PROMPT_MAX (the backend cap).
 * Duplicated here so this module stays pure and dependency-light.
 */
export const PROMPT_MAX = 500;

/** Join phrases as a natural English list: "a", "a and b", "a, b, and c". */
function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/** Pick "A" / "An" based on the first sound of the phrase. */
function article(phrase: string): 'A' | 'An' {
  return /^[aeiou]/i.test(phrase.trim()) ? 'An' : 'A';
}

/**
 * Pure: turn the wizard selections into a single natural-language
 * character-concept prompt. React-free and i18n-free — this is the seam where
 * a future async `enhancePrompt(composed)` (qavatar-api, Phase 2) can wrap the
 * output before it seeds the editable Review textarea.
 *
 * Shape: "A {vibe} {archetype}[, with {a, b, and c}]."
 * Hard-capped at PROMPT_MAX as a backstop (accent count is already bounded).
 */
export function composePrompt(selections: AssistantSelections): string {
  const archetype = CATALOG.archetypes.find((a) => a.id === selections.archetypeId);
  if (!archetype) return '';

  const vibe = CATALOG.vibes.find((v) => v.id === selections.vibeId);
  const accents = selections.accentIds
    .map((id) => CATALOG.accents.find((a) => a.id === id))
    .filter((a): a is NonNullable<typeof a> => Boolean(a));

  const subject = vibe ? `${vibe.promptFragment} ${archetype.promptFragment}` : archetype.promptFragment;
  let sentence = `${article(subject)} ${subject}`;
  if (accents.length > 0) {
    sentence += `, with ${joinWithAnd(accents.map((a) => a.promptFragment))}`;
  }
  sentence += '.';

  return sentence.slice(0, PROMPT_MAX);
}
